<!--
  A phone has no sidebar, so the first notice shows as one bar on the Home
  page only. Every other page shows the page. The More tab has a dot, and the
  More sheet shows the same cards as the sidebar.

  The order: the state of the profile, a team invitation, the time zone, the
  upgrade call. The upgrade call keeps its own bar.
-->
<template>
  <div v-if="isHome" class="md:hidden">
    <AlertUpgradeToPro
      v-if="notice === 'upgrade'"
      :kind="upgradeKind ?? undefined"
    />
    <div
      v-else-if="notice"
      class="flex flex-wrap items-center gap-x-2 gap-y-0.5 border-b border-white/10 bg-neutral-900 px-4 py-3 text-sm text-white/90"
    >
      <template v-if="notice === 'profile' && profileStatus">
        <span>{{ profileLine }}</span>
        <InertiaLink :href="profileStatus.href" class="gm-home-notice-go">
          {{ profileStatus.action }}
          <ArrowRightIcon class="size-3.5 shrink-0" aria-hidden="true" />
        </InertiaLink>
      </template>

      <template v-else-if="notice === 'invite' && teamInvite">
        <span v-if="teamInvite.teamName">
          <b class="font-semibold text-white">{{ teamInvite.teamName }}</b>
          invited you to their team.
        </span>
        <span v-else>You are invited to a team.</span>
        <InertiaLink href="/team/invitations" class="gm-home-notice-go">
          See the invitation
          <ArrowRightIcon class="size-3.5 shrink-0" aria-hidden="true" />
        </InertiaLink>
      </template>

      <template v-else-if="notice === 'timezone' && timezoneAsk">
        <span>
          Times show in {{ zonePlace(timezoneAsk.from) }} time. You are in
          {{ zonePlace(timezoneAsk.to) }}.
        </span>
        <!-- The answer is in the More sheet, next to the next session. -->
        <button
          type="button"
          class="gm-home-notice-go"
          @click="isMoreSheetOpen = true"
        >
          Update
          <ArrowRightIcon class="size-3.5 shrink-0" aria-hidden="true" />
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import ArrowRightIcon from '~icons/lucide/arrow-right'
import AlertUpgradeToPro from '@/shared/components/alerts/AlertUpgradeToPro.vue'
import { useShellNotices } from '@/shared/components/sidebar/useShellNotices'
import { useSidebarState } from '@/shared/components/sidebar/useSidebarState'
import { zonePlace } from '@/shared/composables/useTimezoneSync'

const page = usePage()
const { isMoreSheetOpen } = useSidebarState()
const { profileStatus, teamInvite, timezoneAsk, upgradeKind } =
  useShellNotices()

const isHome = computed(() => page.url.startsWith('/dashboard'))

// "Account not active (40%)." or "Your profile is hidden."
const profileLine = computed(() => {
  const status = profileStatus.value
  if (!status) return ''
  const share = status.percent !== null ? ` (${status.percent}%)` : ''
  return `${status.title}${share}.`
})

const notice = computed(() => {
  if (profileStatus.value) return 'profile'
  if (teamInvite.value) return 'invite'
  if (timezoneAsk.value) return 'timezone'
  if (upgradeKind.value) return 'upgrade'
  return null
})
</script>

<style scoped>
@reference "#tailwind-theme";

.gm-home-notice-go {
  @apply inline-flex items-center gap-1 font-semibold text-white hover:underline;
}
</style>
